import { DataConverter } from "../../../../Global/Base/Converters/DataConverter";
import { CaseInsensitiveMap } from "../../../../Global/Base/DataTypes/CaseInsensitiveMap";
import { BasicDataType } from "../../../../OW.Remoting/Remoting/Cache/ClassDescriptionCache/Interfaces";
import { OWObject } from "../../../../OW.Remoting/Remoting/DataTypes/OWObject";
import { OWManager } from "../../../../OW.Remoting/Remoting/OWManager";
import { ImageEditElement } from "../../../UI.Controls/ImageEditElement";
import { FileEditStatus } from "../FileMenu";
import { UploadManager } from "../UploadManager";
import { ControlParams } from "./ControlInterfaces";
import { FileEdit } from "./FileEdit";

export class ImageEdit extends FileEdit {
    private _imageEdit: ImageEditElement;

    private _objectImageAttributeChangeListener: (sender: OWObject, prm: string) => void;
    private _imageUploaderListeners: CaseInsensitiveMap<() => void> = new CaseInsensitiveMap();

    public override get actualObject(): OWObject {
        return this._actualObject;
    }

    public override set actualObject(value: OWObject) {
        if (this._actualObject != null) {
            this._imageEdit.src = undefined;

            const uploaderData = UploadManager.instance.getUploadData(this._actualObject.instanceID, this.attrName);
            if (uploaderData && uploaderData.uploader) {
                uploaderData.uploader.onStart.remove(this._imageUploaderListeners.get("start"));
                uploaderData.uploader.onFinished.remove(this._imageUploaderListeners.get("finished"));
            }

            if (this._objectImageAttributeChangeListener) {
                this._actualObject.attributeChanged.remove(this._objectImageAttributeChangeListener);
                this._objectImageAttributeChangeListener = null;
            }
        }

        super.actualObject = value;

        if (this._actualObject != null) {
            this._actualObject.attributeChanged.add(this._objectImageAttributeChangeListener =
                (sender, attr) => {
                    this._onAttributeChanged(sender, attr);
                });
            this._updatePreview();
        }
    }

    private _onAttributeChanged(_sender: OWObject, changedAttribute: string): void {
        if (changedAttribute != this.attrName)
            return;

        this._actualObject.getAttributeMetadata(this.attrName).then(attributeMetadata => {
            if (attributeMetadata.basicType == BasicDataType.Binary) {
                this._onBinaryAttributeChanged();
            } else {
                this._onDocumentAttributeChanged();
            }
        });
    }

    private _onBinaryAttributeChanged(): void {
        this._updatePreview();
    }

    private _onDocumentAttributeChanged(): void {
        const uploaderData = UploadManager.instance.getUploadData(this._actualObject.instanceID, this.attrName);
        const uploader = uploaderData.uploader;

        const startListener = () => {
            this._imageEdit.src = undefined;
        };
        this._imageUploaderListeners.set("start", startListener);
        uploader.onStart.add(startListener);
        const finishedListener = () => {
            this._updatePreview();
        };
        this._imageUploaderListeners.set("finished", finishedListener);
        uploader.onFinished.add(finishedListener);

        if (uploader.isComplete)
            this._updatePreview();
    }

    constructor(params: ControlParams) {
        super(params);
        this.element.classList.add("ImageEdit");

        this.enabledChanged.add((_s, v) => {
            this._imageEdit.enabled = v && v.value ? this.isEditing : v.value;
        });

        this.isEditingChanged.add((_s, v) => {
            this._imageEdit.enabled = v.value && this.enabled;
        });
    }

    protected override _createFileElement(): void {
        this._imageEdit = new ImageEditElement();
        this._fileEditElement = this._imageEdit.fileEdit;

        this.element.appendChild(this._imageEdit as HTMLElement);
    }

    protected override _setListeners(): void {
        super._setListeners();
    }

    private async _updatePreview(): Promise<void> {
        if (this._actualObject) {
            const val = await this._actualObject.getValue(this.attrName);

            if (val) {
                try {
                    const attributeMetadata = await this._actualObject.getAttributeMetadata(this.attrName);
                    if (attributeMetadata.basicType == BasicDataType.Binary) {
                        this._imageEdit.src = "data:image/jpeg;base64," + DataConverter.bufferToBase64(val.valueOf());
                    } else {
                        this._imageEdit.src = await OWManager.getDocumentDownloadUrl(this._actualObject.instanceID, this.attrName, "", this._getRequestContext());
                    }
                } catch (e) {
                    this._imageEdit.src = undefined;
                    this._imageEdit.setStatus(FileEditStatus.previewNotSupported); //\e0a3
                }
            } else
                this._imageEdit.src = undefined;
        } else
            this._imageEdit.src = undefined;
    }
    //#endregion Properties

    public override dispose(): void {
        if (this._actualObject) {
            const uploaderData = UploadManager.instance.getUploadData(this._actualObject.instanceID, this.attrName);
            if (uploaderData && uploaderData.uploader) {
                uploaderData.uploader.onStart.remove(this._imageUploaderListeners.get("start"));
                uploaderData.uploader.onFinished.remove(this._imageUploaderListeners.get("finished"));
            }

            if (this._objectImageAttributeChangeListener) {
                this._actualObject.attributeChanged.remove(this._objectImageAttributeChangeListener);
                this._objectImageAttributeChangeListener = null;
            }
        }

        this._actualObject = null;

        super.dispose();
    }
}
